/**
 * Master Admin — Products tab on the Dealer Detail page.
 *
 * iter139 E (2026-07-24) — Reorganized layout ONLY. The previous single
 * long scroll was mixing four different jobs and two audiences. This
 * refactor introduces:
 *   1. A status summary bar at the very top (always visible).
 *   2. Setup / Branding / Campaign / History tabs.
 *   3. Progressive disclosure — template copy blocks collapsed by default,
 *      active-campaign status split from "start new campaign" form (form
 *      hidden behind a button).
 * No behavior, endpoint, validation, or field logic changed. Every control
 * that existed before still exists and does the exact same thing.
 *
 * The dealer never sees these controls — the dealer's own switch lives on
 * TestDrivePage. Master-only.
 */
// iter133e — vetted defaults MIRROR of backend's BRANDING_DEFAULTS.
// Pre-seeded into empty editor fields so master edits real copy,
// not empty boxes, AND used as the fallback in the live preview so
// what master sees while editing = what the embed will render.
const BRANDING_DEFAULTS = {
  headline_a: "Take a test drive, on us.",
  subtext_a:  "Book a spin at {dealer} and we'll text you a {amount} Amazon gift card after your visit.",
  button_a:   "Book my test drive",
  headline_b_bar: "Test drive at {dealer} — get {amount}",
  subtext_b_bar:  "Amazon gift card, texted after you visit. No purchase necessary.",
  headline_b:     "Drive it. Get paid.",
  button_b:       "Book my test drive",
  kicker_c:   "{dealer} · Test drive reward",
  headline_c: "Claim your drive",
  subtext_c:  "Book a test drive, show up, take a spin. We'll text your card the same day — nothing to buy.",
  button_c:   "Book my test drive",
  brand_color:  "#0f172a",
  button_color: "#dc2626",
  logo_url:     "",
};

// Merge saved branding with defaults, treating blank strings as
// "use the default". Same rule the backend applies at render time.
function mergeBrandingWithDefaults(branding = {}) {
  const out = { ...BRANDING_DEFAULTS };
  Object.entries(branding).forEach(([k, v]) => {
    if (v == null) return;
    if (typeof v === "string" && !v.trim()) return;
    out[k] = v;
  });
  return out;
}

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import {
  Save, Loader2, DollarSign, Users, AlertTriangle, Hash, Play, StopCircle,
  Loader as LoaderIcon, Eye, AlertCircle, Settings, Palette, Megaphone, History as HistoryIcon,
  Plus, ChevronDown,
} from "lucide-react";
import TemplateMiniPreview from "../TemplateMiniPreview";
import LogoUploadField from "./LogoUploadField";
import GiftCardVaultPanel from "./GiftCardVaultPanel";
import CampaignTriggerControls from "./CampaignTriggerControls";

export default function DealerProductsTab({ dealer }) {
  const { apiFetch } = useAuth();
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tdDraft, setTdDraft] = useState(null);
  const [newCampaignDraft, setNewCampaignDraft] = useState({ name: "", denomination_cents: 2500 });
  const [campaignBusy, setCampaignBusy] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState("A"); // WYSIWYG toggle
  const [previewMeta, setPreviewMeta] = useState(null); // dealer_name + denomination for the preview
  const [activeTab, setActiveTab] = useState("setup"); // setup | branding | campaign | history
  const [showNewCampaign, setShowNewCampaign] = useState(false); // progressive disclosure

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prodRes, campRes, artRes] = await Promise.all([
        apiFetch(`/admin/dealers/${dealer.id}/products`, { method: "GET" }),
        apiFetch(`/admin/dealers/${dealer.id}/campaigns`, { method: "GET" }).catch(() => ({ data: [] })),
        apiFetch(`/admin/test-drive/artwork`, { method: "GET" }).catch(() => ({ data: { artwork_url: "" } })),
      ]);
      const list = prodRes?.data || [];
      setProducts(list);
      setCampaigns(campRes?.data || []);
      const active = (campRes?.data || []).find((c) => c.status === "active");
      setPreviewMeta({
        dealer_name: dealer.name || "your dealership",
        denomination_cents: active?.denomination_cents ?? 0,
        artwork_url: artRes?.data?.artwork_url || "",
        fixed_microcopy: "No purchase necessary · Gift card sent by text after your visit.",
      });
      const td = list.find((p) => p.code === "test_drive");
      if (td) {
        const seededBranding = mergeBrandingWithDefaults(td.master_config?.branding || {});
        setTdDraft({
          master_active: td.master_active,
          per_person_cap: td.master_config?.per_person_cap ?? 1,
          low_inventory_threshold: td.master_config?.low_inventory_threshold ?? 10,
          volume_cap: td.master_config?.volume_cap ?? "",
          branding: seededBranding,
          _savedBranding: seededBranding,
        });
      }
    } catch (e) {
      toast?.({ title: "Failed to load products", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [dealer.id, dealer.name, apiFetch, toast]);

  useEffect(() => { load(); }, [load]);

  const saveTestDrive = async () => {
    if (!tdDraft || saving) return;
    setSaving(true);
    try {
      const master_config = {
        per_person_cap: Number(tdDraft.per_person_cap) || 1,
        low_inventory_threshold: Number(tdDraft.low_inventory_threshold) || 10,
      };
      if (tdDraft.volume_cap !== "" && tdDraft.volume_cap != null) {
        master_config.volume_cap = Number(tdDraft.volume_cap);
      }
      if (tdDraft.branding && Object.keys(tdDraft.branding).length) {
        master_config.branding = tdDraft.branding;
      }
      await apiFetch(`/admin/dealers/${dealer.id}/products/test_drive`, {
        method: "PATCH",
        data: { master_active: !!tdDraft.master_active, master_config },
      });
      toast?.({ title: "Test Drive saved" });
      setTdDraft((s) => s ? { ...s, _savedBranding: { ...(s.branding || {}) } } : s);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      toast?.({ title: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const startCampaign = async () => {
    if (campaignBusy) return;
    if (!newCampaignDraft.name.trim()) {
      toast?.({ title: "Campaign name is required", variant: "destructive" });
      return;
    }
    setCampaignBusy(true);
    try {
      await apiFetch(`/admin/dealers/${dealer.id}/campaigns`, {
        method: "POST",
        data: {
          name: newCampaignDraft.name.trim(),
          denomination_cents: Number(newCampaignDraft.denomination_cents),
        },
      });
      toast?.({ title: "Campaign started" });
      setNewCampaignDraft({ name: "", denomination_cents: 2500 });
      setShowNewCampaign(false);
      await load();
    } catch (e) {
      toast?.({ title: e?.response?.data?.detail || "Start failed", variant: "destructive" });
    } finally { setCampaignBusy(false); }
  };

  const endCampaign = async (campaign) => {
    if (campaignBusy) return;
    if (!window.confirm(`End campaign "${campaign.name}"? Leftover codes reconcile separately.`)) return;
    setCampaignBusy(true);
    try {
      await apiFetch(`/admin/dealers/${dealer.id}/campaigns/${campaign.id}/end`, { method: "POST" });
      toast?.({ title: "Campaign ended" });
      await load();
    } catch (e) {
      toast?.({ title: e?.response?.data?.detail || "End failed", variant: "destructive" });
    } finally { setCampaignBusy(false); }
  };

  const reactivateCampaign = async (campaign) => {
    if (campaignBusy) return;
    const msg = `Reactivate popup for "${campaign.name}"?\n\nThis will resume the popup on the dealer's site and accept new bookings immediately. Uploads of new codes must be complete before you continue.`;
    if (!window.confirm(msg)) return;
    setCampaignBusy(true);
    try {
      const r = await apiFetch(
        `/admin/dealers/${dealer.id}/campaigns/${campaign.id}/reactivate`,
        { method: "POST" }
      );
      toast?.({ title: `Popup reactivated · ${r?.data?.available_codes || 0} codes ready` });
      await load();
    } catch (e) {
      toast?.({
        title: e?.response?.data?.detail || "Reactivation failed",
        variant: "destructive",
      });
    } finally { setCampaignBusy(false); }
  };

  const activeCampaign = campaigns.find((c) => c.status === "active");
  const endedCampaigns = campaigns.filter((c) => c.status === "ended");

  if (loading) {
    return (
      <div className="p-4 flex items-center gap-2 text-gray-600" data-testid="admin-products-loading">
        <Loader2 className="animate-spin" size={16} /> Loading products…
      </div>
    );
  }

  const td = products.find((p) => p.code === "test_drive");

  // ── Status computation (pure derivation, no side effects) ─────────
  const dealerSwitchOn = !!td?.dealer_active;
  const masterOn = !!tdDraft?.master_active;
  const overrideOff = !!activeCampaign?.master_override_off;
  const suspended = activeCampaign?.system_status &&
                    activeCampaign.system_status !== "active";

  let stateLabel, stateColor;
  if (!masterOn) {
    stateLabel = "Entitlement Off"; stateColor = "gray";
  } else if (overrideOff) {
    stateLabel = "Kill-switch: OFF"; stateColor = "gray";
  } else if (suspended) {
    stateLabel = activeCampaign.system_status === "suspended_no_inventory"
      ? "Paused — awaiting inventory"
      : `Paused — ${activeCampaign.system_status}`;
    stateColor = "amber";
  } else if (!activeCampaign) {
    stateLabel = "No active campaign"; stateColor = "gray";
  } else if (!dealerSwitchOn) {
    stateLabel = "Off — dealer switch off"; stateColor = "gray";
  } else {
    stateLabel = "Live"; stateColor = "green";
  }

  const amountDollars = activeCampaign
    ? `$${(activeCampaign.denomination_cents / 100).toFixed(0)}`
    : "—";
  const triggerLabel = activeCampaign?.trigger_mode || "—";
  const codesAvail = activeCampaign?.available_codes_count ?? 0;
  const testMode = !!activeCampaign?.test_mode;
  const alwaysMode = activeCampaign?.trigger_mode === "always";

  return (
    <div className="space-y-4" data-testid="admin-dealer-products-tab">
      {/* ═══ Status summary bar ═════════════════════════════════════ */}
      <section
        className="bg-white rounded-lg border border-gray-200 px-4 py-3"
        data-testid="admin-td-status-bar"
      >
        <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-[12.5px]">
          <span className="font-semibold text-gray-900">
            {dealer.name || "Dealer"} · Test Drive
          </span>
          <StatePill color={stateColor} label={stateLabel} testId="admin-td-state-pill" />
          <StatusItem label="Amount" value={amountDollars} testId="admin-td-status-amount" />
          <StatusItem label="Trigger" value={triggerLabel} testId="admin-td-status-trigger" />
          <StatusItem label="Codes available" value={codesAvail} testId="admin-td-status-codes" />
          <StatusItem
            label="Dealer switch"
            value={dealerSwitchOn ? "ON" : "OFF"}
            valueColor={dealerSwitchOn ? "text-green-700" : "text-gray-600"}
            testId="admin-td-status-dealer-switch"
          />
          {testMode && (
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-200 text-amber-900"
              data-testid="admin-td-chip-test-mode"
            >
              TEST MODE
            </span>
          )}
          {alwaysMode && !testMode && (
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-red-600 text-white"
              data-testid="admin-td-chip-always-spend"
            >
              ALWAYS — SPENDING REAL BUDGET
            </span>
          )}
        </div>
      </section>

      {/* ═══ Tab nav ════════════════════════════════════════════════ */}
      <div
        className="flex items-center gap-1 border-b border-gray-200"
        role="tablist"
        data-testid="admin-td-tab-nav"
      >
        {[
          { key: "setup",    label: "Setup",    icon: <Settings size={13} /> },
          { key: "branding", label: "Branding", icon: <Palette size={13} /> },
          { key: "campaign", label: "Campaign", icon: <Megaphone size={13} /> },
          { key: "history",  label: "History",  icon: <HistoryIcon size={13} /> },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeTab === t.key}
            onClick={() => setActiveTab(t.key)}
            data-testid={`admin-td-tab-${t.key}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === t.key
                ? "border-red-600 text-red-700"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            {t.icon} {t.label}
            {t.key === "history" && endedCampaigns.length > 0 && (
              <span className="ml-1 text-[10px] px-1.5 rounded-full bg-gray-200 text-gray-700">
                {endedCampaigns.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ═══ SETUP tab ══════════════════════════════════════════════ */}
      {activeTab === "setup" && (
        <section
          className="bg-white rounded-lg border border-gray-200 p-5"
          data-testid="admin-td-tab-panel-setup"
        >
          <header className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="text-[15px] font-semibold text-gray-900">Setup — entitlement &amp; money</h3>
              <p className="text-[12px] text-gray-500 mt-0.5">
                Entitle this dealer to run gift-card-incentivized test drive campaigns. Master
                controls the money — the dealer controls the switch.
              </p>
            </div>
            <MasterSwitch
              testId="admin-td-master-switch"
              checked={!!tdDraft?.master_active}
              onChange={(v) => setTdDraft((s) => ({ ...s, master_active: v }))}
            />
          </header>

          {tdDraft?.master_active && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Per-customer cap" icon={<Users size={14} />}>
                <input
                  type="number"
                  min={1}
                  step={1}
                  className="input w-full"
                  data-testid="admin-td-per-person-cap"
                  value={tdDraft.per_person_cap ?? 1}
                  onChange={(e) => setTdDraft((s) => ({ ...s, per_person_cap: e.target.value }))}
                />
              </Field>
              <Field label="Low-inventory alert threshold" icon={<AlertTriangle size={14} />}>
                <input
                  type="number"
                  min={1}
                  step={1}
                  className="input w-full"
                  data-testid="admin-td-low-inv-threshold"
                  value={tdDraft.low_inventory_threshold ?? 10}
                  onChange={(e) =>
                    setTdDraft((s) => ({ ...s, low_inventory_threshold: e.target.value }))
                  }
                />
              </Field>
              <Field label="Volume cap (optional)" icon={<Hash size={14} />}>
                <input
                  type="number"
                  min={0}
                  step={1}
                  className="input w-full"
                  data-testid="admin-td-volume-cap"
                  value={tdDraft.volume_cap ?? ""}
                  onChange={(e) => setTdDraft((s) => ({ ...s, volume_cap: e.target.value }))}
                  placeholder="No cap"
                />
              </Field>
            </div>
          )}

          <div className="mt-5 flex items-center justify-between">
            <div className="text-[12px] text-gray-500" data-testid="admin-td-live-status">
              Live for dealer:{" "}
              <span className={td?.live ? "text-green-700 font-semibold" : "text-gray-700"}>
                {td?.live ? "Yes (dealer switched on)" : `No (dealer switch: ${td?.dealer_active ? "on" : "off"})`}
              </span>
            </div>
            <button
              type="button"
              className="btn-primary flex items-center gap-2"
              onClick={saveTestDrive}
              disabled={saving}
              data-testid="admin-td-save"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save Test Drive
            </button>
          </div>

          {/* Unlock — Phase 1 note lives at the bottom of Setup, since it's about entitlement. */}
          <div
            className="mt-5 pt-4 border-t border-gray-100 text-[12.5px] text-gray-600"
            data-testid="admin-unlock-note"
          >
            <span className="font-semibold text-gray-800">Unlock Price</span> is still managed on the
            Overview tab via the legacy toggle. It will migrate into this Products tab in a future
            iteration.
          </div>
        </section>
      )}

      {/* ═══ BRANDING tab ═══════════════════════════════════════════ */}
      {activeTab === "branding" && (
        <section
          className="bg-white rounded-lg border border-gray-200 p-5 space-y-4"
          data-testid="admin-td-tab-panel-branding"
        >
          <header className="flex items-center gap-2 flex-wrap">
            <DollarSign size={16} className="text-slate-500" />
            <h3 className="text-[15px] font-semibold text-gray-900">Test Drive Branding</h3>
            {tdDraft?.branding && tdDraft?._savedBranding &&
              JSON.stringify(tdDraft.branding) !== JSON.stringify(tdDraft._savedBranding) && (
                <span
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5"
                  data-testid="admin-td-branding-dirty"
                >
                  <AlertCircle size={11} /> Unsaved changes
                </span>
              )}
            <button
              type="button"
              className="ml-auto btn-primary flex items-center gap-2 text-[12px]"
              onClick={saveTestDrive}
              disabled={saving || !tdDraft?.master_active}
              data-testid="admin-td-save-branding"
              title={!tdDraft?.master_active ? "Turn on the master entitlement (Setup tab) to save branding." : ""}
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save
            </button>
          </header>
          <p className="text-[12px] text-gray-500">
            Edit the copy visitors see. <code>{"{dealer}"}</code> renders the dealer&apos;s name;{" "}
            <code>{"{amount}"}</code> renders the active campaign amount. The dealer picks the frame
            variant — you control everything inside it. Any field you leave blank falls back to the
            vetted default at render time.
          </p>

          {!tdDraft?.master_active && (
            <div className="text-[12px] bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">
              Master entitlement is OFF. Branding edits are visible in the preview below but you must
              turn on the entitlement (Setup tab) before Save will persist.
            </div>
          )}

          {/* Live WYSIWYG preview — always visible. */}
          <div
            className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3"
            data-testid="admin-td-wysiwyg-preview"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Eye size={14} className="text-slate-600" />
                <span className="text-[12.5px] font-semibold text-slate-800">
                  Preview (draft — updates as you type)
                </span>
              </div>
              <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md p-0.5" role="tablist">
                {["A", "B", "C"].map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setPreviewTemplate(k)}
                    role="tab"
                    aria-selected={previewTemplate === k}
                    data-testid={`admin-td-preview-tab-${k.toLowerCase()}`}
                    className={`px-2.5 py-1 text-[11px] font-semibold rounded ${
                      previewTemplate === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    Template {k}
                  </button>
                ))}
              </div>
            </div>
            <div className="max-w-[420px]">
              <TemplateMiniPreview
                template={previewTemplate}
                preview={{
                  ...(previewMeta || {}),
                  branding: mergeBrandingWithDefaults(tdDraft?.branding || {}),
                }}
              />
            </div>
            <p className="text-[11px] text-slate-500">
              This preview reflects your unsaved edits. Click <strong>Save</strong> above to make it
              live on the dealer&apos;s site.
            </p>
          </div>

          <BrandingEditor
            value={tdDraft?.branding || {}}
            onChange={(patch) => setTdDraft((s) => ({ ...s, branding: { ...(s.branding || {}), ...patch } }))}
          />
          <div className="text-[11px] text-gray-500">
            Amazon artwork is a platform-level asset —{" "}
            <em>set once via <code>TEST_DRIVE_ARTWORK_URL</code> env var, reused across all dealers</em>.
            Per Amazon&apos;s brand guidelines, don&apos;t alter the logo and don&apos;t imply Amazon endorses the dealer or the offer.
          </div>
        </section>
      )}

      {/* ═══ CAMPAIGN tab ═══════════════════════════════════════════ */}
      {activeTab === "campaign" && (
        <section
          className="bg-white rounded-lg border border-gray-200 p-5 space-y-4"
          data-testid="admin-td-tab-panel-campaign"
        >
          <header className="flex items-center gap-2">
            <DollarSign size={16} className="text-red-500" />
            <h3 className="text-[15px] font-semibold text-gray-900">Campaign</h3>
          </header>
          <p className="text-[12px] text-gray-500">
            The active campaign sets the denomination shown on the dealer&apos;s site. Only one campaign
            can be active at a time. Denomination is immutable per campaign — to change the amount,
            end this one and start a new campaign.
          </p>

          {activeCampaign ? (
            <>
              {/* ── Active campaign STATUS CARD (read-only header). ── */}
              <div
                className="bg-red-50 border border-red-200 rounded-md p-3"
                data-testid="admin-td-active-campaign"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-[13px] font-semibold text-red-900 flex items-center gap-2 flex-wrap">
                      {activeCampaign.name} — ${(activeCampaign.denomination_cents / 100).toFixed(0)}
                      {activeCampaign.system_status && activeCampaign.system_status !== "active" && (
                        <span
                          className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-200 text-amber-900"
                          data-testid="admin-td-system-status-badge"
                        >
                          {activeCampaign.system_status === "suspended_no_inventory"
                            ? "PAUSED · NO INVENTORY"
                            : activeCampaign.system_status.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-red-700">
                      Started {new Date(activeCampaign.started_at).toLocaleDateString()} · Status: active
                      {activeCampaign.system_status_reason && (
                        <span className="text-amber-800"> · {activeCampaign.system_status_reason}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-red-800 mt-1">
                      Trigger: <strong>{activeCampaign.trigger_mode || "engaged"}</strong>
                      {" · "}Codes available/issued: <strong>{activeCampaign.available_codes_count ?? 0}</strong>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {activeCampaign.system_status && activeCampaign.system_status !== "active" && (
                      <button
                        type="button"
                        onClick={() => reactivateCampaign(activeCampaign)}
                        disabled={campaignBusy || (activeCampaign.available_codes_count || 0) === 0}
                        title={
                          (activeCampaign.available_codes_count || 0) === 0
                            ? "Upload at least one valid Amazon-format code before reactivating — the vault has 0 available codes for this campaign."
                            : "Reactivate the popup on the dealer's site. Bookings will be accepted immediately."
                        }
                        className="flex items-center gap-1 text-[12px] font-semibold text-emerald-800 hover:text-emerald-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="admin-td-reactivate-campaign"
                      >
                        <Play size={14} /> Reactivate popup
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => endCampaign(activeCampaign)}
                      disabled={campaignBusy}
                      className="flex items-center gap-1 text-[12px] font-semibold text-red-700 hover:text-red-900 disabled:opacity-50"
                      data-testid="admin-td-end-campaign"
                    >
                      <StopCircle size={14} /> End
                    </button>
                  </div>
                </div>
              </div>

              {/* ── Gift-card inventory + Trigger controls (operations). ── */}
              <GiftCardVaultPanel campaign={activeCampaign} onInventoryChange={load} />
              <CampaignTriggerControls
                campaign={activeCampaign}
                dealerId={dealer.id}
                onSaved={load}
              />

              {/* ── "Start new campaign" behind a button ─────────────────
                   Progressive disclosure — reading the current campaign
                   never looks like starting a new one. */}
              <div className="pt-3 border-t border-gray-100" data-testid="admin-td-new-campaign-wrap">
                {!showNewCampaign ? (
                  <button
                    type="button"
                    onClick={() => setShowNewCampaign(true)}
                    className="flex items-center gap-1.5 text-[12px] font-semibold text-gray-700 hover:text-gray-900"
                    data-testid="admin-td-show-new-campaign"
                  >
                    <Plus size={13} /> Start new campaign / switch amount
                    <ChevronDown size={12} />
                  </button>
                ) : (
                  <NewCampaignForm
                    draft={newCampaignDraft}
                    setDraft={setNewCampaignDraft}
                    onSubmit={startCampaign}
                    busy={campaignBusy}
                    disabled={!!activeCampaign}
                    onCancel={() => setShowNewCampaign(false)}
                    activeCampaign={activeCampaign}
                  />
                )}
              </div>
            </>
          ) : (
            <>
              <div className="bg-gray-50 border border-dashed border-gray-300 rounded-md p-3">
                <div className="text-[13px] font-semibold text-gray-700">No active campaign</div>
                <div className="text-[11px] text-gray-500">
                  Start one below — the popup won&apos;t render until a campaign is active.
                </div>
              </div>
              {/* When nothing is active the form is inline (no button reveal needed). */}
              <NewCampaignForm
                draft={newCampaignDraft}
                setDraft={setNewCampaignDraft}
                onSubmit={startCampaign}
                busy={campaignBusy}
                disabled={false}
                activeCampaign={null}
              />
            </>
          )}
        </section>
      )}

      {/* ═══ HISTORY tab ════════════════════════════════════════════ */}
      {activeTab === "history" && (
        <section
          className="bg-white rounded-lg border border-gray-200 p-5"
          data-testid="admin-td-tab-panel-history"
        >
          <header className="flex items-center gap-2 mb-3">
            <HistoryIcon size={16} className="text-gray-500" />
            <h3 className="text-[15px] font-semibold text-gray-900">Campaign history</h3>
          </header>
          {endedCampaigns.length === 0 ? (
            <div className="text-[12px] text-gray-500" data-testid="admin-td-history-empty">
              No ended campaigns yet.
            </div>
          ) : (
            <ul className="text-[12px] text-gray-700 space-y-1">
              {endedCampaigns.map((c) => (
                <li
                  key={c.id}
                  data-testid={`admin-td-history-${c.id}`}
                  className="flex items-center gap-2 py-1 border-b border-gray-100 last:border-0"
                >
                  <span className="font-semibold">{c.name}</span>
                  <span className="text-gray-500">
                    — ${(c.denomination_cents / 100).toFixed(0)}
                  </span>
                  <span className="text-gray-500 ml-auto">
                    ended {c.ended_at ? new Date(c.ended_at).toLocaleDateString() : "—"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}


// ── Presentational helpers ──────────────────────────────────────────

function StatusItem({ label, value, valueColor = "text-gray-900", testId }) {
  return (
    <span className="inline-flex items-center gap-1 text-gray-500">
      <span>{label}:</span>
      <span
        className={`font-semibold ${valueColor}`}
        data-testid={testId}
      >
        {value}
      </span>
    </span>
  );
}

function StatePill({ label, color, testId }) {
  const palette = {
    green: "bg-green-100 text-green-800 border-green-300",
    amber: "bg-amber-100 text-amber-900 border-amber-300",
    gray:  "bg-gray-100 text-gray-700 border-gray-300",
  }[color] || "bg-gray-100 text-gray-700 border-gray-300";
  const dot = { green: "bg-green-500", amber: "bg-amber-500", gray: "bg-gray-400" }[color] || "bg-gray-400";
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold border rounded-full px-2 py-0.5 ${palette}`}
      data-testid={testId}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

function MasterSwitch({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 ${
        checked ? "bg-red-600" : "bg-gray-300"
      }`}
    >
      <span
        aria-hidden
        className={`inline-block h-5 w-5 mt-0.5 ml-0.5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

function Field({ label, icon, children }) {
  return (
    <label className="block">
      <span className="text-[12px] font-medium text-gray-700 flex items-center gap-1.5 mb-1">
        {icon} {label}
      </span>
      {children}
    </label>
  );
}

// Extracted so the same form can render inline (no active campaign) or
// behind the "Start new campaign" reveal (active campaign present).
function NewCampaignForm({ draft, setDraft, onSubmit, busy, disabled, activeCampaign, onCancel }) {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end"
      data-testid="admin-td-new-campaign-form"
    >
      <Field label="New campaign name">
        <input
          type="text"
          className="input w-full"
          data-testid="admin-td-new-campaign-name"
          placeholder="e.g. July 2026 $25 promo"
          value={draft.name}
          onChange={(e) => setDraft((s) => ({ ...s, name: e.target.value }))}
          disabled={disabled}
        />
      </Field>
      <Field label="Denomination ($)">
        <input
          type="number"
          min={1}
          step={1}
          className="input w-full"
          data-testid="admin-td-new-campaign-denom"
          value={Math.round(draft.denomination_cents / 100)}
          onChange={(e) => setDraft((s) => ({ ...s, denomination_cents: Number(e.target.value) * 100 }))}
          disabled={disabled}
        />
      </Field>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy || disabled}
          className="btn-primary flex items-center justify-center gap-2 h-[38px] flex-1"
          data-testid="admin-td-start-campaign"
          title={
            disabled && activeCampaign
              ? "End the active campaign before starting a new one."
              : ""
          }
        >
          {busy ? <LoaderIcon size={14} className="animate-spin" /> : <Play size={14} />}
          Start campaign
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-[12px] text-gray-600 hover:text-gray-900 px-2 h-[38px]"
            data-testid="admin-td-cancel-new-campaign"
          >
            Cancel
          </button>
        )}
      </div>
      {disabled && activeCampaign && (
        <div className="sm:col-span-3 text-[11px] text-gray-500">
          A campaign is currently active. End &ldquo;{activeCampaign.name}&rdquo; above before starting a new one.
        </div>
      )}
    </div>
  );
}


// iter133c — Test Drive Branding Editor (master-only). Grouped by
// template with a shared color + logo row up top. Amount is NEVER
// editable here — it comes from the active campaign.
// iter139 E — all template blocks default to COLLAPSED (previously A was open)
// so the branding tab default view is clean; live preview stays visible above.
function BrandingEditor({ value, onChange }) {
  const v = value || {};
  const set = (k) => (e) => onChange({ [k]: e.target.value });
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="block text-[12px]">
          <span className="block font-medium text-gray-700 mb-1">Brand color</span>
          <div className="flex items-center gap-2">
            <input type="color" data-testid="admin-td-brand-color" value={v.brand_color || "#0f172a"} onChange={set("brand_color")} className="w-10 h-9 border border-gray-300 rounded" />
            <input type="text" value={v.brand_color || "#0f172a"} onChange={set("brand_color")} className="input flex-1 font-mono text-[12px]" />
          </div>
        </label>
        <label className="block text-[12px]">
          <span className="block font-medium text-gray-700 mb-1">Button color</span>
          <div className="flex items-center gap-2">
            <input type="color" data-testid="admin-td-button-color" value={v.button_color || "#dc2626"} onChange={set("button_color")} className="w-10 h-9 border border-gray-300 rounded" />
            <input type="text" value={v.button_color || "#dc2626"} onChange={set("button_color")} className="input flex-1 font-mono text-[12px]" />
          </div>
        </label>
        <label className="block text-[12px]">
          <span className="block font-medium text-gray-700 mb-1">Dealer logo (optional)</span>
          <LogoUploadField
            value={v.logo_url || ""}
            onChange={(val) => onChange({ logo_url: val })}
            testId="admin-td-logo-upload"
          />
        </label>
      </div>

      {["a", "b", "c"].map((letter) => (
        <details
          key={letter}
          className="border border-gray-200 rounded-md"
          data-testid={`admin-td-copy-block-${letter}`}
        >
          <summary className="cursor-pointer select-none px-4 py-2 text-[13px] font-semibold text-gray-800 bg-gray-50">
            Template {letter.toUpperCase()} copy
          </summary>
          <div className="p-4 space-y-3">
            {letter === "b" && (
              <>
                <label className="block text-[12px]">
                  <span className="block font-medium text-gray-700 mb-1">Bar headline</span>
                  <input type="text" value={v.headline_b_bar || ""} onChange={set("headline_b_bar")} data-testid="admin-td-headline_b_bar" className="input w-full" placeholder="Test drive at {dealer} — get {amount}" />
                </label>
                <label className="block text-[12px]">
                  <span className="block font-medium text-gray-700 mb-1">Bar subtext</span>
                  <input type="text" value={v.subtext_b_bar || ""} onChange={set("subtext_b_bar")} data-testid="admin-td-subtext_b_bar" className="input w-full" />
                </label>
              </>
            )}
            {letter === "c" && (
              <label className="block text-[12px]">
                <span className="block font-medium text-gray-700 mb-1">Kicker</span>
                <input type="text" value={v.kicker_c || ""} onChange={set("kicker_c")} data-testid="admin-td-kicker_c" className="input w-full" placeholder="{dealer} · Test drive reward" />
              </label>
            )}
            <label className="block text-[12px]">
              <span className="block font-medium text-gray-700 mb-1">Headline</span>
              <input type="text" value={v[`headline_${letter}`] || ""} onChange={set(`headline_${letter}`)} data-testid={`admin-td-headline_${letter}`} className="input w-full" />
            </label>
            <label className="block text-[12px]">
              <span className="block font-medium text-gray-700 mb-1">Subtext</span>
              <textarea rows={2} value={v[`subtext_${letter}`] || ""} onChange={set(`subtext_${letter}`)} data-testid={`admin-td-subtext_${letter}`} className="input w-full" />
            </label>
            <label className="block text-[12px]">
              <span className="block font-medium text-gray-700 mb-1">Button label</span>
              <input type="text" value={v[`button_${letter}`] || ""} onChange={set(`button_${letter}`)} data-testid={`admin-td-button_${letter}`} className="input w-full" />
            </label>
          </div>
        </details>
      ))}
    </div>
  );
}
